import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { MinimalConversation } from "@redotech/redo-model/conversation";
import { GetUser as User } from "@redotech/redo-model/user";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { ProgressBar } from "@redotech/redo-web/progress-bar";
import { memo, useEffect, useState } from "react";
import {
  bulkAssignConversations,
  updateConversation,
} from "../client/conversations";
import { AssigneePickerInline } from "../conversation-detail-panel/assignee-picker-dropdown";
import { getNextBatch } from "../utils";

export const AssignTicketModal = memo(function AssignTicketModal({
  open,
  setOpen,
  conversations,
  shouldAssignToMeImmediately,
  setShouldAssignToMeImmediately,
  cleanupFn,
  selectAllInfo,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  conversations: MinimalConversation[];
  shouldAssignToMeImmediately: boolean;
  setShouldAssignToMeImmediately: (val: boolean) => void;
  cleanupFn?: (clearSelected?: boolean) => void;
  selectAllInfo?: {
    count: number;
    deselectedConversations: MinimalConversation[];
    bulkActionIterator: AsyncIterator<{ conversations: MinimalConversation[] }>;
    setBlockRefresh: (val: boolean) => void;
  };
}) {
  const user = useRequiredContext(UserContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const [assigningSelectAll, setAssigningSelectAll] = useState(false);
  const [numDone, setNumDone] = useState(0);
  const defaultAssignee = shouldAssignToMeImmediately
    ? user
    : conversations.length === 1 ||
        (conversations.length > 1 &&
          conversations.every(
            (conversation) =>
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              conversation.assignee?._id === conversations[0].assignee?._id,
          ))
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        conversations[0].assignee
      : undefined;
  const [assignee, setAssignee] = useState<User | null>(
    defaultAssignee ?? null,
  );

  const handleAssignTickets = async () => {
    if (conversations.length === 1) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      await updateConversation(client, conversations[0], {
        assignee: assignee?._id || null,
      });
      amplitude.logEvent("update-conversationAssignee", {
        mode: "single",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        conversationIds: [conversations[0]._id],
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        channels: [conversations[0].platform],
        assignee: assignee?._id,
      });
    } else if (conversations.length > 1) {
      const conversationIds = conversations
        .filter(
          (conversation) =>
            (conversation.assignee?._id || null) !== (assignee?._id || null),
        )
        .map((conversation) => conversation._id);
      if (conversationIds.length > 0) {
        await bulkAssignConversations(client, {
          conversationIds,
          assignee: assignee?._id || null,
        });
        const channels = [
          ...new Set(
            conversations.map((conversation) => conversation.platform),
          ),
        ];
        amplitude.logEvent("update-conversationAssignee", {
          mode: "multiple",
          conversationIds,
          channels,
          assignee: assignee?._id,
        });
      }
    }
    cleanupFn?.(true);
    setOpen(false);
  };

  const assignBatch = async (
    assignee: User | null,
  ): Promise<{ batchSize: number; done: boolean }> => {
    if (!selectAllInfo) return { batchSize: 0, done: true };
    const { done, value } = await getNextBatch(
      selectAllInfo.bulkActionIterator,
    );
    if (done) {
      return { batchSize: 0, done };
    }
    const conversationIds = value.conversations
      .filter(
        (conversation) =>
          (conversation.assignee?._id || null) !== (assignee?._id || null) && // TODO
          !selectAllInfo.deselectedConversations.some(
            (deselectedConversation) =>
              deselectedConversation._id === conversation._id,
          ),
      )
      .map((conversation) => conversation._id);
    if (conversationIds.length > 0) {
      await bulkAssignConversations(client, {
        conversationIds,
        assignee: assignee?._id || null,
      });
    }
    return { batchSize: value.conversations.length, done: false };
  };

  // This function synchronously assigns each page of conversations.
  // If speed becomes an issue, we could asynchronously assign each page.
  const handleAssignSelectAll = async () => {
    if (!selectAllInfo) return;
    setAssigningSelectAll(true);
    selectAllInfo.setBlockRefresh(true);
    let done = false;
    do {
      const result = await assignBatch(assignee);
      setNumDone((prevNumDone) => prevNumDone + result.batchSize);
      done = result.done;
    } while (!done);
    amplitude.logEvent("update-conversationAssignee", { mode: "all" });
    setAssigningSelectAll(false);
    selectAllInfo.setBlockRefresh(false);
    cleanupFn?.(true);
    setOpen(false);
  };

  useEffect(() => {
    if (shouldAssignToMeImmediately) {
      (async () => {
        if (selectAllInfo) {
          await handleAssignSelectAll();
        } else {
          await handleAssignTickets();
        }
        setShouldAssignToMeImmediately(false);
      })();
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAssignToMeImmediately]);

  const plural = conversations.length > 1 || selectAllInfo;

  return (
    <RedoModal
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={() => {
        cleanupFn?.();
        setOpen(false);
      }}
      primaryButton={
        assigningSelectAll
          ? undefined
          : {
              text: assignee === null ? "Unassign" : "Assign",
              onClick: () =>
                selectAllInfo ? handleAssignSelectAll() : handleAssignTickets(),
              disabled: (assignee?._id || null) === defaultAssignee?._id,
              pending: shouldAssignToMeImmediately,
              size: "md",
            }
      }
      secondaryButton={
        assigningSelectAll
          ? undefined
          : { text: "Cancel", onClick: () => setOpen(false), size: "md" }
      }
      title={
        assigningSelectAll
          ? "Assigning tickets"
          : `Assign ${
              selectAllInfo
                ? "all selected "
                : plural
                  ? conversations.length + " "
                  : ""
            }ticket${plural ? "s" : ""}?`
      }
    >
      {assigningSelectAll ? (
        <ProgressBar
          value={(numDone / (selectAllInfo?.count || numDone)) * 100}
        />
      ) : (
        <AssigneePickerInline
          assignee={assignee}
          onAssigneeChange={setAssignee}
        />
      )}
    </RedoModal>
  );
});
